import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import QueryWrapper from 'graphql-client/QueryWrapper';
import type {MoleculeHeavyAtomCountQuery} from 'model-hub/__generated/MoleculeHeavyAtomCountQuery';
import type {ProteinAminoAcidsQuery} from 'model-hub/__generated/ProteinAminoAcidsQuery';
import type {ProteinStructurePredictionPermissionsQuery} from 'model-hub/__generated/ProteinStructurePredictionPermissionsQuery';

export const MOLECULE_HEAVY_ATOM_COUNT_QUERY = gql`
  query MoleculeHeavyAtomCountQuery($moleculeIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: CHEMISTRY) {
    molecules(ids: $moleculeIds) {
      ... on MoleculeNode {
        id
        folder {
          id
        }
        currentVersion {
          id
          chemicalStructure {
            id
            heavyAtomCount {
              ... on IntPropertyNode {
                value
              }
            }
          }
        }
        permissions {
          readable
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const MOLECULE_HEAVY_ATOM_COUNT_QUERY_WRAPPER: QueryWrapper<
  MoleculeHeavyAtomCountQuery,
  {moleculeIds: Array<string>}
> = {
  query: MOLECULE_HEAVY_ATOM_COUNT_QUERY,
};

export const PREDICTION_JOB_RESULTS_DOWNLOAD_URL_QUERY = gql`
  query PredictionJobResultsDownloadUrlQuery($id: StrictString!) @skipAudit @engComponent(name: MODEL_HUB) {
    predictionJob(id: $id) {
      id
      resultsDownloadUrl
    }
  }
`;

export const PREDICTION_BATCH_DETAIL_QUERY = gql`
  query PredictionBatchDetailQuery($id: StrictString!) @skipAudit @engComponent(name: MODEL_HUB) {
    predictionBatch(id: $id) {
      id
      name
      createdAt
      modifiedAt
      modelName
      statusCode
      numPredictionJobs
      creator {
        id
        name
      }
    }
  }
`;

export const PREDICTION_DETAIL_QUERY = gql`
  query PredictionDetailQuery($id: StrictString!) @skipAudit @engComponent(name: MODEL_HUB) {
    predictionJob(id: $id) {
      id
      createdAt
      modifiedAt
      modelName
      statusCode
      componentApiIds
      configJson
      hasResultsFile
      resultsDownloadUrl
      standardizedOutputFileName
      standardizedOutputDownloadUrl
      predictionBatch {
        id
        name
      }
    }
  }
`;

export const PREDICTION_JOB_MOLECULE_SMILES_QUERY = gql`
  query PredictionJobMoleculeSmilesQuery($moleculeIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: CHEMISTRY) {
    molecules(ids: $moleculeIds) {
      ... on MoleculeNode {
        id
        currentVersion {
          id
          chemicalStructure {
            id
            canonicalSmiles
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const PROTEIN_AMINO_ACIDS_QUERY: DocumentNode = gql`
  query ProteinAminoAcidsQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: MOLBIO) {
    proteins(ids: $ids) {
      ... on ProteinNode {
        id
        folder {
          id
        }
        experimentalCurrentVersion {
          dbId
          aminoAcids
        }
        permissions {
          readable
          polymerFeaturesEditable
          structurePredictionEditable
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const PROTEIN_AMINO_ACIDS_QUERY_WRAPPER: QueryWrapper<ProteinAminoAcidsQuery, {ids: Array<string>}> = {
  query: PROTEIN_AMINO_ACIDS_QUERY,
};

export const PROTEIN_STRUCTURE_PREDICTION_PERMISSIONS_QUERY: DocumentNode = gql`
  query ProteinStructurePredictionPermissionsQuery($ids: [StrictString!]!)
  @skipAudit
  @engComponent(name: MOLBIO) {
    proteins(ids: $ids) {
      __typename
      ... on ProteinNode {
        id
        permissions {
          structurePredictionEditable
        }
      }
    }
  }
`;

export const PROTEIN_STRUCTURE_PREDICTION_PERMISSIONS_QUERY_WRAPPER: QueryWrapper<
  ProteinStructurePredictionPermissionsQuery,
  {ids: Array<string>}
> = {
  query: PROTEIN_STRUCTURE_PREDICTION_PERMISSIONS_QUERY,
};
